/**
 * Personalized Image Banner
 * Handles Salesforce Data 360 CDP integration for personalized banner experiences
 * Shows only the experience that matches the CDP API response
 */

/* Guarded: this file is <script src>'d once per banner section instance, so a
   page with more than one personalized banner loads it more than once. */
(function () {
  if (window.PersonalizedImageBanner) return;

  class PersonalizedImageBanner {
    constructor(moduleElement) {
      this.module = moduleElement;
      this.sectionId = moduleElement.dataset.sectionId;
      this.maxWaitTime = parseInt(moduleElement.dataset.maxWaitTime) || 2000;
      this.cdpEnabled = moduleElement.dataset.cdpEnabled === 'true';
      this.cdpEnvironment = window.D360ThemeSettings?.selectedEnvironment || 'Development';
      this.personalizationPoint = moduleElement.dataset.personalizationPoint;
      this.fallbackExperience = moduleElement.dataset.fallbackExperience;
      this.mockResponse = moduleElement.dataset.mockResponse;

      // Get all banner experience blocks
      this.bannerBlocks = Array.from(this.module.querySelectorAll('[data-experience-id]'));

      // Personalization tracking properties
      this.currentPersonalizationData = null;
      this.isPersonalizedExperience = false;
      this.viewEventFired = false;
      this.intersectionObserver = null;

      // Resolve banner block up to 3 times, then hide section and stop
      this.maxResolveAttempts = 3;
      this.resolveRetryDelayMs = 500;
      this.resolveStopped = false;

      this.init();
    }

    /**
     * Initialize personalization process
     */
    async init() {
      if (!this.cdpEnabled) {
        // Hide skeleton when CDP is disabled
        const skeletonWrapper = this.module.querySelector('[data-skeleton]');
        if (skeletonWrapper) {
          skeletonWrapper.style.setProperty('display', 'none', 'important');
        }
        // When personalization is disabled, show only the fallback experience
        await this.resolveAndShowExperience(this.fallbackExperience);
        return;
      }

      // Clear existing matching experience cookie to ensure fallback takes precedence
      if (this.fallbackExperience) {
        const existingCookie = document.cookie.split('; ').find((row) => row.startsWith('d360_matching_experience='));
        if (existingCookie) {
          document.cookie = 'd360_matching_experience=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
      }

      // Add loading state to all blocks
      this.addLoadingState();

      try {
        const personalizationResult = await this.getPersonalizedExperience();

        // If no visitor cookie or not personalized, show fallback experience
        if (personalizationResult.experienceId === null || !personalizationResult.isPersonalized) {
          // Clear any existing personalization data to prevent events
          this.isPersonalizedExperience = false;
          this.currentPersonalizationData = null;
          await this.resolveAndShowExperience(this.fallbackExperience);
          return;
        }

        // Store personalization data for event tracking only for personalized experiences
        this.isPersonalizedExperience = personalizationResult.isPersonalized;
        this.currentPersonalizationData = personalizationResult.personalizationData;

        await this.resolveAndShowExperience(personalizationResult.experienceId);
      } catch {
        // Silent fallback in production
        this.isPersonalizedExperience = false;
        this.currentPersonalizationData = null;
        await this.resolveAndShowExperience(this.fallbackExperience);
      } finally {
        this.removeLoadingState();
      }
    }

    /**
     * Get personalized experience using shared D360 utilities
     */
    async getPersonalizedExperience() {
      if (!window.D360Utils) {
        console.error('D360 utilities not loaded');
        return this.fallbackExperience;
      }

      const config = {
        personalizationPointName: this.personalizationPoint,
        fallbackExperienceId: this.fallbackExperience,
        maxWaitTime: this.maxWaitTime,
        environment: this.cdpEnvironment,
        mockResponse: this.mockResponse,
        enableDebugLogging: false,
      };

      return await window.D360Utils.getPersonalizedExperience(config);
    }

    /**
     * Refresh banner block list (blocks may render after first paint)
     */
    refreshBannerBlocks() {
      this.bannerBlocks = Array.from(this.module.querySelectorAll('[data-experience-id]'));
    }

    /**
     * IDs to try when resolving which banner to show (requested, then fallback if different)
     */
    getExperienceIdsToTry(primaryExperienceId) {
      const ids = [];
      const primaryId = primaryExperienceId != null && primaryExperienceId !== '' ? primaryExperienceId.toString() : '';
      const fallbackId =
        this.fallbackExperience != null && this.fallbackExperience !== '' ? this.fallbackExperience.toString() : '';

      if (primaryId) {
        ids.push(primaryId);
      }
      if (fallbackId && fallbackId !== primaryId) {
        ids.push(fallbackId);
      }
      return ids;
    }

    findBannerForExperienceId(experienceId) {
      const requestedId = experienceId != null && experienceId !== '' ? experienceId.toString() : '';
      if (!requestedId) {
        return null;
      }
      return this.bannerBlocks.find((block) => block.dataset.experienceId === requestedId) || null;
    }

    /**
     * Try up to 3 times to match a banner block; then hide section and stop further checks
     */
    async resolveAndShowExperience(experienceId) {
      if (this.resolveStopped) {
        return;
      }

      const idsToTry = this.getExperienceIdsToTry(experienceId);

      if (idsToTry.length === 0) {
        this.hideSection();
        return;
      }

      for (let attempt = 1; attempt <= this.maxResolveAttempts; attempt++) {
        if (this.resolveStopped) {
          return;
        }

        this.refreshBannerBlocks();

        for (const id of idsToTry) {
          const matchingBanner = this.findBannerForExperienceId(id);
          if (matchingBanner) {
            if (id !== idsToTry[0]) {
              this.isPersonalizedExperience = false;
              this.currentPersonalizationData = null;
            }
            this.displayExperience(id, matchingBanner);
            return;
          }
        }

        if (attempt < this.maxResolveAttempts) {
          if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
            console.warn(
              `No banner found for Experience_ID(s) [${idsToTry.join(', ')}] (attempt ${attempt}/${this.maxResolveAttempts}). Retrying…`,
            );
          }
          await this.delay(this.resolveRetryDelayMs);
        }
      }

      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        console.warn(
          `No banner found for Experience_ID(s) [${idsToTry.join(', ')}] after ${this.maxResolveAttempts} attempts. Hiding section.`,
        );
      }
      this.hideSection();
    }

    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Show one matched banner experience and hide all others (no recursion)
     */
    displayExperience(experienceId, matchingBanner) {
      const requestedId = experienceId.toString();

      this.bannerBlocks.forEach((block) => {
        if (block.dataset.experienceId === requestedId) {
          this.showBanner(block);
        } else {
          this.hideBanner(block);
        }
      });

      // CRITICAL: Always attach click tracking for the rendered banner (both personalized AND fallback)
      this.setupClickTracking(matchingBanner);

      // Only attach D360 view observer for personalized renders
      if (this.isPersonalizedExperience && this.currentPersonalizationData) {
        this.setupIntersectionObserver(matchingBanner);
      }
    }

    /**
     * Hide entire personalized banner section and stop further resolve attempts
     */
    hideSection() {
      this.resolveStopped = true;
      this.isPersonalizedExperience = false;
      this.currentPersonalizationData = null;

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      this.refreshBannerBlocks();
      this.bannerBlocks.forEach((block) => this.hideBanner(block));

      const skeletonWrapper = this.module.querySelector('[data-skeleton]');
      if (skeletonWrapper) {
        skeletonWrapper.style.setProperty('display', 'none', 'important');
      }

      this.module.style.setProperty('display', 'none', 'important');

      const shopifySection = this.module.closest('.shopify-section');
      if (shopifySection) {
        shopifySection.style.setProperty('display', 'none', 'important');
      }
    }

    /**
     * Show a banner block and its parent wrapper
     */
    showBanner(bannerElement) {
      if (bannerElement) {
        bannerElement.style.setProperty('display', 'block', 'important');
        // Also show the parent wrapper
        const parentWrapper = bannerElement.closest('.banner-experience-wrapper');
        if (parentWrapper) {
          parentWrapper.style.setProperty('display', 'block', 'important');
        }
      }
    }

    /**
     * Hide a banner block and its parent wrapper
     */
    hideBanner(bannerElement) {
      if (bannerElement) {
        bannerElement.style.setProperty('display', 'none', 'important');
        // Also hide the parent wrapper to remove padding
        const parentWrapper = bannerElement.closest('.banner-experience-wrapper');
        if (parentWrapper) {
          parentWrapper.style.setProperty('display', 'none', 'important');
        }
      }
    }

    /**
     * Add loading state to all banners
     */
    addLoadingState() {
      this.module.classList.add('loading');
      // Don't set opacity here - let CSS handle the skeleton state
    }

    /**
     * Remove loading state from all banners
     */
    removeLoadingState() {
      // Add a small delay to ensure smooth transition
      setTimeout(() => {
        this.module.classList.remove('loading');

        // Explicitly hide the skeleton wrapper to override inline styles
        const skeletonWrapper = this.module.querySelector('[data-skeleton]');
        if (skeletonWrapper) {
          skeletonWrapper.style.setProperty('display', 'none', 'important');
        }
      }, 100);
    }

    /**
     * Set up personalization tracking for view and click events
     */
    setupPersonalizationTracking(bannerElement) {
      // Set up IntersectionObserver for view event
      this.setupIntersectionObserver(bannerElement);

      // Set up click event listeners for CTAs
      this.setupClickTracking(bannerElement);
    }

    /**
     * Set up IntersectionObserver to fire view event when banner enters viewport
     */
    setupIntersectionObserver(bannerElement) {
      if (this.viewEventFired || !this.currentPersonalizationData) {
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.viewEventFired) {
              this.fireViewEvent();
              this.viewEventFired = true;
              // Disconnect observer after first trigger
              if (this.intersectionObserver) {
                this.intersectionObserver.disconnect();
                this.intersectionObserver = null;
              }
            }
          });
        },
        {
          threshold: 0.5, // Fire when 50% of banner is visible
        },
      );

      this.intersectionObserver.observe(bannerElement);
    }

    /**
     * Set up click event listeners for all CTA elements in the banner
     * Tracks both AEP analytics (sessionStorage) and D360 personalization events
     */
    setupClickTracking(bannerElement) {
      if (!bannerElement || bannerElement.dataset.clickTrackingInitialized === 'true') {
        return;
      }
      bannerElement.dataset.clickTrackingInitialized = 'true';

      const experienceId = bannerElement.dataset.experienceId || this.fallbackExperience || '1';
      const bannerLink = bannerElement.dataset.bannerlink;
      const openInNewTab = bannerElement.dataset.newtab === 'true';

      // 1. Banner-level body click (captures clicks ANYWHERE over the banner, linked or unlinked)
      const handleBannerClick = (e) => {
        // Prevent race condition on rapid consecutive clicks
        if (bannerElement.dataset.processing === 'true') {
          return;
        }

        // Prevent duplicate event if a CTA button, link, or video control was clicked (they handle their own events)
        if (e.target.closest('a.button, .banner__content a, .banner-video-wrapper, button')) {
          return;
        }

        bannerElement.dataset.processing = 'true';

        // Write AEP click-data synchronously
        this.writeAEPClickData(bannerElement, bannerElement, experienceId);

        // Fire D360 personalization-click event (only for personalized experiences)
        if (this.isPersonalizedExperience && this.currentPersonalizationData) {
          this.fireClickEvent();
        }

        // If banner has a link configured, execute navigation
        if (bannerLink) {
          if (openInNewTab) {
            window.open(bannerLink, '_blank');
            setTimeout(() => {
              bannerElement.dataset.processing = 'false';
            }, 400);
          } else {
            window.location.href = bannerLink;
          }
        } else {
          // Unlinked banner click: release processing lock after debounce
          setTimeout(() => {
            bannerElement.dataset.processing = 'false';
          }, 400);
        }
      };

      bannerElement.addEventListener('click', handleBannerClick);
      bannerElement.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          handleBannerClick(e);
        }
      });

      // 2. All buttons and links within the banner content (CTA clicks - <a> tags navigate natively)
      const buttonsAndLinks = bannerElement.querySelectorAll('a.button, .banner__content a');
      buttonsAndLinks.forEach((element) => {
        element.addEventListener('click', () => {
          if (element.dataset.processing === 'true') {
            return;
          }
          element.dataset.processing = 'true';

          // Write AEP click-data synchronously BEFORE navigation
          this.writeAEPClickData(element, bannerElement, experienceId);

          // Fire D360 personalization-click event (only for personalized experiences)
          if (this.isPersonalizedExperience && this.currentPersonalizationData) {
            this.fireClickEvent();
          }

          setTimeout(() => {
            element.dataset.processing = 'false';
          }, 400);
        });
      });

      // 3. Set up video control tracking if video exists
      this.setupVideoTracking(bannerElement, experienceId);
    }

    /**
     * Write AEP click-data to sessionStorage for next page analytics
     * Implements MDX-3357 requirements for per-experience click tracking
     *
     * @param {HTMLElement} clickedElement - The clicked element (banner or CTA)
     * @param {HTMLElement} bannerElement - The parent banner element
     * @param {string} experienceId - Experience ID from block settings
     */
    writeAEPClickData(clickedElement, bannerElement, experienceId) {
      const altText = bannerElement.dataset.alt || 'image banner';
      const expId = experienceId || this.fallbackExperience || '1';

      // Determine link title based on click target
      let linkTitle;
      let isCTAClick = false;

      // Check if this is a CTA button/link click (not banner body)
      if (
        clickedElement.classList.contains('button') ||
        (clickedElement.tagName === 'A' && clickedElement !== bannerElement)
      ) {
        // CTA click - use button text
        linkTitle = clickedElement.textContent.trim();
        isCTAClick = true;
      } else {
        // Banner body click - use alt text
        linkTitle = altText;
      }

      // Get target URL
      const targetURL = clickedElement.href || bannerElement.dataset.bannerlink || '';

      // Get page template name from module, body, or default to index
      const pageType =
        this.module?.dataset?.pageType ||
        document.documentElement.dataset.template ||
        document.body.dataset.template ||
        'index';

      // Build click-data payload following standard Image Banner pattern
      const clickData = {
        moduleName: `main:personalizedImageBanner:exp-${expId}`,
        linkTitle: linkTitle,
        linkText: 'image', // Always "image" per requirements (matches standard banner)
        targetURL: targetURL,
        pageName: window.location.pathname,
        pageType: pageType,
        locURL: window.location.href,
        filters: null,
        searchTerm: null,
      };

      // CRITICAL: Synchronous write (no setTimeout) to prevent race condition
      // This ensures click-data is persisted before navigation occurs
      sessionStorage.setItem('click-data', JSON.stringify(clickData));

      // If there is no target URL (unlinked click), publish immediate in-page event
      if (!targetURL) {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish('page-click', { clickData });
        }
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'page-click', clickData });
        }
      }

      // Debug logging in dev environments
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        console.log('AEP click-data written:', {
          experienceId: expId,
          clickType: isCTAClick ? 'CTA' : 'Banner Body',
          clickData,
        });
      }
    }

    /**
     * Set up video play/pause control tracking
     * Fires immediate page-click event (no navigation)
     *
     * @param {HTMLElement} bannerElement - The parent banner element
     * @param {string} experienceId - Experience ID from block settings
     */
    setupVideoTracking(bannerElement, experienceId) {
      // Find video control buttons (play/pause)
      const videoControls = bannerElement.querySelectorAll(
        '.banner-video-play-pause, .video-play-pause, .slideshow-video-play-pause, [data-video-control]',
      );

      if (videoControls.length === 0) {
        return; // No video controls found
      }

      videoControls.forEach((control) => {
        control.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent banner navigation

          // CRITICAL FIX: Read the action from button's data-linkText attribute
          // This attribute is maintained by image-banner-video.js and represents
          // what action the button WILL perform when clicked:
          // - When video is paused → button shows Play icon → linkText = 'videoPlay'
          // - When video is playing → button shows Pause icon → linkText = 'videoPause'
          const action = control.dataset.linkText || 'videoPlay';

          // Get page template name
          const pageType =
            this.module?.dataset?.pageType ||
            document.documentElement.dataset.template ||
            document.body.dataset.template ||
            'index';

          const clickData = {
            moduleName: `main:personalizedImageBanner:exp-${experienceId}`,
            linkTitle: 'image banner',
            linkText: action, // "videoPlay" or "videoPause"
            targetURL: '', // No navigation for video controls
            pageName: window.location.pathname,
            pageType: pageType,
            locURL: window.location.href,
            filters: null,
            searchTerm: null,
          };

          // Write to sessionStorage
          sessionStorage.setItem('click-data', JSON.stringify(clickData));

          // Fire immediate page-click event through Shopify analytics
          if (window.Shopify?.analytics?.publish) {
            window.Shopify.analytics.publish('page-click', {
              clickData: clickData,
            });
          }

          // Fallback for dataLayer if present
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'page-click',
              clickData: clickData,
            });
          }

          // Debug logging
          if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
            console.log('Video control click tracked:', {
              experienceId,
              action,
              clickData,
            });
          }
        });
      });
    }

    /**
     * Fire personalization view event
     */
    fireViewEvent() {
      if (!this.currentPersonalizationData || !window.D360Utils) {
        return;
      }

      window.D360Utils.publishPersonalizationViewEvent(this.currentPersonalizationData);

      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        console.log('Personalization view event fired:', this.currentPersonalizationData);
      }
    }

    /**
     * Fire personalization click event
     */
    fireClickEvent() {
      if (!this.currentPersonalizationData || !window.D360Utils) {
        return;
      }

      window.D360Utils.publishPersonalizationClickEvent(this.currentPersonalizationData);

      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        console.log('Personalization click event fired:', this.currentPersonalizationData);
      }
    }
  }

  // Initialize personalized banner when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    const personalizedModules = document.querySelectorAll('personalized-banner-module');

    personalizedModules.forEach((module) => {
      new PersonalizedImageBanner(module);
    });
  });

  /**
   * Re-initialize when sections are dynamically loaded (for Shopify theme dynamic sections)
   */
  document.addEventListener('shopify:section:load', (event) => {
    const personalizedModule = event.target.querySelector('personalized-banner-module');
    if (personalizedModule) {
      new PersonalizedImageBanner(personalizedModule);
    }
  });

  // Export for potential use by other scripts
  window.PersonalizedImageBanner = PersonalizedImageBanner;
})();
